import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, fmtUSD, vendorLabel } from "../lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

import { VENDOR_SLUGS } from "../lib/api";

const empty = { vendor: "electronics", name: "", price: 0, image: "", description: "", in_stock: true };

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const r = await api.get("/products");
    setProducts(r.data);
  };

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user]);

  if (loading) return <div className="p-16 text-white/40">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!user.is_admin) return <Navigate to="/admin/login" replace />;

  const filtered = filter === "all" ? products : products.filter((p) => p.vendor === filter);

  const openAdd = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(`/products`, payload);
        toast.success("Added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Could not save");
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="flex items-center justify-between flex-wrap gap-6">
        <div>
          <div className="overline mb-2">Control room</div>
          <h1 className="font-serif italic text-4xl">Catalog · {products.length} items</h1>
          <p className="text-white/50 text-sm mt-1">Signed in as {user.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="text-xs uppercase tracking-[0.22em] text-white/60 hover:text-white">View store</Link>
          <button
            type="button"
            onClick={openAdd}
            data-testid="add-product-button"
            className="inline-flex items-center gap-2 px-5 py-3 bg-white text-black uppercase tracking-[0.22em] text-xs font-semibold"
          >
            <Plus className="w-4 h-4" /> Add product
          </button>
          <button
            type="button"
            onClick={logout}
            data-testid="logout-button"
            className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 uppercase tracking-[0.22em] text-xs font-semibold"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {["all", ...VENDOR_SLUGS].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilter(s)}
            data-testid={`admin-filter-${s}`}
            className={`px-3 py-2 text-[11px] uppercase tracking-[0.22em] border ${filter === s ? "bg-white text-black border-white" : "border-white/15 text-white/60 hover:text-white"}`}
          >
            {s === "all" ? "All" : vendorLabel(s)}
          </button>
        ))}
      </div>

      <div className="mt-6 border border-white/10">
        <div className="grid grid-cols-12 px-4 py-3 text-[11px] uppercase tracking-[0.22em] text-white/40 border-b border-white/10">
          <div className="col-span-1">Img</div>
          <div className="col-span-4">Name</div>
          <div className="col-span-2">Vendor</div>
          <div className="col-span-2">Price</div>
          <div className="col-span-2">Stock</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {filtered.map((p) => (
          <div key={p.id} data-testid={`admin-row-${p.id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-white/5 hover:bg-white/[0.02]">
            <div className="col-span-1"><img src={p.image} alt="" className="w-10 h-12 object-cover bg-white/5" /></div>
            <div className="col-span-4 truncate text-sm">{p.name}</div>
            <div className="col-span-2 text-xs text-white/60">{vendorLabel(p.vendor)}</div>
            <div className="col-span-2 text-sm tabular-nums">{fmtUSD(p.price)}</div>
            <div className="col-span-2 text-xs text-white/60">{p.in_stock ? "In stock" : "Out"}</div>
            <div className="col-span-1 flex items-center justify-end gap-2">
              <button type="button" onClick={() => openEdit(p)} data-testid={`edit-${p.id}`} className="p-2 hover:bg-white/10"><Pencil className="w-4 h-4" /></button>
              <button type="button" onClick={() => del(p)} data-testid={`delete-${p.id}`} className="p-2 hover:bg-white/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-dialog" className="bg-[#0A0A0C] text-white border border-white/10 max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-serif italic text-2xl">
              {editing ? "Edit product" : "Add product"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.22em] text-white/60">Name</Label>
              <Input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-2 bg-white/5 border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.22em] text-white/60">Vendor</Label>
                <select data-testid="form-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 px-3 py-2 text-sm rounded">
                  {VENDOR_SLUGS.map((v) => (<option key={v} value={v} className="bg-[#0A0A0C]">{vendorLabel(v)}</option>))}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.22em] text-white/60">Price (USD)</Label>
                <Input data-testid="form-price" type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required className="mt-2 bg-white/5 border-white/10" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.22em] text-white/60">Image URL</Label>
              <Input data-testid="form-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} required className="mt-2 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.22em] text-white/60">Description</Label>
              <Textarea data-testid="form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required className="mt-2 bg-white/5 border-white/10" />
            </div>
            <label className="flex items-center gap-3 text-sm text-white/70">
              <input type="checkbox" data-testid="form-in-stock" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} />
              In stock
            </label>
            <button type="submit" data-testid="form-submit" className="w-full py-3 bg-white text-black uppercase tracking-[0.22em] text-xs font-semibold">
              {editing ? "Save changes" : "Create product"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
